import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BuildVsBuyDialog } from "@/components/admin/BuildVsBuyDialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Target,
  DollarSign,
  Calendar,
  Building2,
  Users,
  Lightbulb,
  Rocket,
  Factory,
  FileText,
  Upload,
  ClipboardCheck,
  Eye,
  File,
  CheckCircle2,
  Wrench,
  Scale,
  TrendingUp,
  Database,
  BarChart3,
  Bot,
  Code,
  Zap,
  Sparkles,
  Loader2,
  Brain,
  Download,
  MessageSquare,
  User,
  Shield,
  Pencil,
  Eye as EyeIcon,
  Clock,
  Plus,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ProjectEditForm } from "@/components/admin/portfolio/ProjectEditForm";
import { ProjectRisksTab } from "@/components/admin/portfolio/ProjectRisksTab";
import { ProjectRoiEditor } from "@/components/admin/portfolio/ProjectRoiEditor";
import { ProjectRubricAnswers } from "@/components/admin/portfolio/ProjectRubricAnswers";

import { LIDER_IA_COMPONENTS, DIMENSION_COLORS, DIMENSION_NAMES } from "@/data/liderIAEvaluationData";
import { useEvaluationWeights } from "@/hooks/useEvaluationWeights";
import { useOrgCurrency } from "@/hooks/useOrgCurrency";
import { formatCurrency as fmtCurrency } from "@/lib/formatCurrency";
import { useProject, useProjectTools, useProjectCollaborators, solutionTypeToKey } from "@/hooks/useProjects";
import { useProjectEvaluation, useEvaluateProject, getEvaluationEligibility, type ComponentScore } from "@/hooks/useProjectEvaluations";
import { useProjectFullExport } from "@/hooks/useProjectFullExport";
import { generateProjectPDF } from "@/lib/generateProjectPDF";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { signDeliverableUrl } from "@/lib/signDeliverable";
import { toast } from "sonner";

// ── Types & Config ──────────────────────────────────────────────

const SOLUTION_TYPES_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  content: { label: "Creación de Contenido con IA", icon: FileText, color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  vibecoding: { label: "Vibecoding y Creación de Aplicaciones con IA", icon: Code, color: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  assistant: { label: "Asistente / Agente IA", icon: Bot, color: "bg-green-500/10 text-green-600 border-green-500/20" },
  automation: { label: "Automatización IA", icon: Zap, color: "bg-orange-500/10 text-orange-600 border-orange-500/20" },
  data: { label: "Análisis de Datos con IA", icon: BarChart3, color: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20" },
};

const statusLabels: Record<string, string> = { ideation: "Ideación", in_progress: "En progreso", completed: "Completado", paused: "Pausado" };
const statusColors: Record<string, string> = {
  ideation: "bg-info/20 text-info border-info/30",
  in_progress: "bg-warning/20 text-warning border-warning/30",
  completed: "bg-success/20 text-success border-success/30",
  paused: "bg-muted text-muted-foreground border-border",
};
const statusIcons: Record<string, React.ReactNode> = {
  ideation: <Lightbulb className="w-4 h-4" />,
  in_progress: <Rocket className="w-4 h-4" />,
  completed: <Factory className="w-4 h-4" />,
};

function getClassification(score: number) {
  if (score >= 400) return { label: "Quick Win", color: "bg-success/20 text-success border-success/30" };
  if (score >= 300) return { label: "Strategic Play", color: "bg-primary/20 text-primary border-primary/30" };
  if (score >= 200) return { label: "Experiment", color: "bg-warning/20 text-warning border-warning/30" };
  return { label: "Back-log", color: "bg-muted text-muted-foreground border-border" };
}

const AVAILABLE_TOOLS: Record<string, { name: string; icon: string }> = {
  lovable: { name: "Lovable", icon: "💜" },
  n8n: { name: "n8n", icon: "🔗" },
  gemini: { name: "Google Gemini", icon: "✨" },
  chatgpt: { name: "ChatGPT", icon: "🤖" },
  claude: { name: "Claude", icon: "🧠" },
  midjourney: { name: "Midjourney", icon: "🎨" },
  make: { name: "Make", icon: "⚡" },
  zapier: { name: "Zapier", icon: "⚙️" },
  "notion-ai": { name: "Notion AI", icon: "📝" },
  copilot: { name: "GitHub Copilot", icon: "👨‍💻" },
  cursor: { name: "Cursor", icon: "🖱️" },
  perplexity: { name: "Perplexity", icon: "🔍" },
  runway: { name: "Runway", icon: "🎬" },
  elevenlabs: { name: "ElevenLabs", icon: "🎙️" },
  huggingface: { name: "Hugging Face", icon: "🤗" },
};

function InfoField({ label, icon, value }: { label: string; icon: React.ReactNode; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">{icon} {label}</p>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────

export default function AdminProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentRole } = useAuth();
  const isAuditor = currentRole === 'auditor'; // solo lectura: ocultar acciones de escritura
  const [activeTab, setActiveTab] = useState("overview");
  const [buildVsBuyOpen, setBuildVsBuyOpen] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const evaluateProject = useEvaluateProject();
  // Real data hooks
  const { data: projectRow, isLoading: projectLoading } = useProject(projectId);
  const { data: toolsData } = useProjectTools(projectId);
  const { data: collaborators = [] } = useProjectCollaborators(projectId);
  const { data: evaluation, isLoading: evalLoading } = useProjectEvaluation(projectId);

  // Get participant profile
  const { data: participant } = useQuery({
    queryKey: ["admin-project-participant", projectRow?.user_id],
    queryFn: async () => {
      if (!projectRow?.user_id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, company, area, email, organization_id")
        .eq("id", projectRow.user_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!projectRow?.user_id,
  });

  // Get cohort name
  const { data: cohort } = useQuery({
    queryKey: ["admin-project-cohort", projectRow?.cohort_id],
    queryFn: async () => {
      if (!projectRow?.cohort_id) return null;
      const { data, error } = await supabase
        .from("cohorts")
        .select("id, name, organization_id")
        .eq("id", projectRow.cohort_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!projectRow?.cohort_id,
  });

  const { getWeight } = useEvaluationWeights(cohort?.organization_id);
  const orgIdForCurrency = (cohort?.organization_id || (participant as any)?.organization_id) ?? null;
  const currency = useOrgCurrency(orgIdForCurrency);

  const { refetch: refetchExport } = useProjectFullExport(projectId, projectRow?.user_id);



  // Sesión exacta que originó este proyecto (1-a-1)
  const sourceSessionId = (projectRow as any)?.source_session_id as string | null | undefined;

  const { data: sourceSession } = useQuery({
    queryKey: ["admin-project-source-session", sourceSessionId],
    enabled: !!sourceSessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_sessions")
        .select("id, title, context_type, created_at, updated_at")
        .eq("id", sourceSessionId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: conversationMessages = [], isLoading: msgsLoading } = useQuery({
    queryKey: ["admin-project-source-conv-msgs", sourceSessionId],
    enabled: !!sourceSessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, role, content, created_at")
        .eq("session_id", sourceSessionId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  if (projectLoading || evalLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!projectRow) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-muted-foreground">Proyecto no encontrado</p>
        <Button onClick={() => navigate("/ai-portfolio")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Portafolio
        </Button>
      </div>
    );
  }

  const solutionTypeKey = solutionTypeToKey(projectRow.solution_type);
  const solutionConfig = SOLUTION_TYPES_CONFIG[solutionTypeKey];
  const projectStatus = projectRow.status || "ideation";
  const totalScore = evaluation?.total_score || 0;
  const classification = getClassification(totalScore);
  const rubricAnswers = (projectRow as any).rubric_answers as Record<string, string> || {};
  const completedAnswers = Object.values(rubricAnswers).filter((a) => (a || "").trim().length >= 20).length;
  const tools = toolsData?.map((t: any) => t.tool_name) || [];

  const getScoreColor = (score: number, max: number = 500) => {
    const pct = (score / max) * 100;
    if (pct >= 80) return "text-green-600";
    if (pct >= 60) return "text-amber-600";
    return "text-red-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/ai-portfolio")} className="shrink-0 mt-1">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-3 mb-1">
            <h1 className="text-2xl font-bold text-foreground">{projectRow.name}</h1>
            <Badge className={`${statusColors[projectStatus] || ""} border gap-1`}>
              {statusIcons[projectStatus]}
              {statusLabels[projectStatus] || projectStatus}
            </Badge>
            {totalScore > 0 && <Badge className={`${classification.color} border`}>{classification.label}</Badge>}
            {solutionConfig && (
              <Badge className={`${solutionConfig.color} border gap-1`}>
                {(() => { const I = solutionConfig.icon; return <I className="w-3 h-3" />; })()}
                {solutionConfig.label}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            {participant?.company && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{participant.company}</span>}
            {cohort?.name && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{cohort.name}</span>}
            {participant?.area && <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{participant.area}</span>}
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{projectRow.created_at?.split("T")[0]}</span>
            {participant?.full_name && <span className="flex items-center gap-1">👤 {participant.full_name}</span>}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 shrink-0">
          <div className="text-center">
            <p className="text-3xl font-bold text-foreground">{totalScore}</p>
            <p className="text-xs text-muted-foreground">/ 500 pts</p>
          </div>
          {!isAuditor && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setBuildVsBuyOpen(true)}>
            <Scale className="w-3.5 h-3.5" />
            Build / Buy / Outsource
          </Button>
          )}
          {!isAuditor && (() => {
            const eligibility = getEvaluationEligibility({
              submitted: (projectRow as any).submitted,
              status: projectRow.status,
              rubric_answers: rubricAnswers,
            });
            const btn = (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                disabled={isEvaluating || !eligibility.eligible}
                onClick={async () => {
                  setIsEvaluating(true);
                  try {
                    await evaluateProject.mutateAsync(projectId!);
                    toast.success("Proyecto re-evaluado por IA");
                  } catch (err: any) {
                    toast.error(err.message || "Error al evaluar");
                  } finally {
                    setIsEvaluating(false);
                  }
                }}
              >
                {isEvaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
                {evaluation ? "Re-evaluar" : "Evaluar IA"}
              </Button>
            );
            return eligibility.eligible ? btn : (
              <span title={eligibility.reason || "No elegible"} className="inline-flex">{btn}</span>
            );
          })()}
          <Button
            size="sm"
            className="gap-1.5 text-xs gradient-primary"
            disabled={isExporting}
            onClick={async () => {
              setIsExporting(true);
              try {
                const extra = await refetchExport();
                await generateProjectPDF({
                  project: projectRow,
                  participant,
                  cohort,
                  tools,
                  evaluation: evaluation
                    ? {
                        total_score: evaluation.total_score,
                        classification: evaluation.classification,
                        component_scores: evaluation.component_scores as ComponentScore[] | null,
                        general_comments: evaluation.general_comments,
                        suggestions: evaluation.suggestions,
                      }
                    : null,
                  deliverables: extra.data?.deliverables || [],
                  buildBuy: extra.data?.buildBuy || null,
                  suggestions: extra.data?.suggestions || [],
                });
                toast.success("PDF generado");
              } catch (err: any) {
                console.error(err);
                toast.error(err.message || "Error al generar PDF");
              } finally {
                setIsExporting(false);
              }
            }}
          >
            {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Descargar PDF
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex lg:grid-cols-none">
          <TabsTrigger value="overview" className="gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Evaluación</span>
          </TabsTrigger>
          <TabsTrigger value="info" className="gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Información</span>
          </TabsTrigger>
          <TabsTrigger value="rubric" className="gap-2">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Rúbrica</span>
          </TabsTrigger>
          <TabsTrigger value="deliverable" className="gap-2">
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Entregable</span>
          </TabsTrigger>
          <TabsTrigger value="risks" className="gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Riesgos</span>
          </TabsTrigger>
          <TabsTrigger value="conversation" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Conversación</span>
          </TabsTrigger>
          <TabsTrigger value="roi" className="gap-2">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">ROI</span>
          </TabsTrigger>
        </TabsList>


        {/* ── Tab: Evaluación LIDER-IA ── */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                Evaluación LIDER-IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {evaluation && evaluation.component_scores ? (
                <>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${getScoreColor(totalScore)}`}>{totalScore}</p>
                      <p className="text-xs text-muted-foreground">/ 500 pts</p>
                    </div>
                    <Progress value={(totalScore / 500) * 100} className="flex-1 h-3" />
                  </div>
                  <div className="space-y-3">
                    {(["L", "I", "D", "E", "R"] as const).map((dim) => {
                      const dimScores = (evaluation.component_scores || []).filter((s: ComponentScore) => s.dimension === dim);
                      if (dimScores.length === 0) return null;
                      return (
                        <div key={dim}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <Badge className={`${DIMENSION_COLORS[dim]} border-0 text-xs font-bold`}>{dim}</Badge>
                            <span className="text-xs font-medium text-muted-foreground">{DIMENSION_NAMES[dim]}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 ml-6">
                            {dimScores.map((cs: ComponentScore) => (
                              <div key={cs.componentId} className="space-y-1">
                                <div className="flex items-center justify-between gap-2 text-xs py-1 px-2 rounded bg-muted/40">
                                  <span className="text-foreground truncate">{cs.name}</span>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <Progress value={(cs.score / 5) * 100} className="w-12 h-1.5" />
                                    <span className="font-semibold text-foreground w-6 text-right">{cs.score.toFixed(1)}</span>
                                  </div>
                                </div>
                                <p className="text-[11px] text-muted-foreground ml-2">{cs.feedback}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* General Comments */}
                  {evaluation.general_comments && (
                    <div className="space-y-2 pt-4">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Brain className="w-4 h-4 text-primary" />
                        Comentarios de la IA
                      </h4>
                      <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                        {evaluation.general_comments}
                      </p>
                    </div>
                  )}

                  {/* Suggestions */}
                  {evaluation.suggestions && evaluation.suggestions.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        Sugerencias
                      </h4>
                      <ul className="space-y-1.5">
                        {evaluation.suggestions.map((s, i) => (
                          <li key={i} className="text-sm text-muted-foreground bg-amber-50 dark:bg-amber-900/10 p-2 rounded border border-amber-200 dark:border-amber-800/30">
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Brain className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Este proyecto aún no ha sido evaluado por IA</p>
                  <p className="text-xs mt-1">Ve a Evaluaciones de Proyectos para ejecutar la evaluación</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Información del Proyecto ── */}
        <TabsContent value="info" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Información del Proyecto
                </CardTitle>
                <CardDescription>Datos ingresados por el participante</CardDescription>
              </div>
              {!isEditing && !isAuditor && (
                <Button variant="outline" size="sm" className="shrink-0" onClick={() => setIsEditing(true)}>
                  <Pencil className="w-4 h-4 mr-1.5" /> Editar
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              {isEditing ? (
                <ProjectEditForm
                  project={projectRow}
                  tools={tools}
                  availableTools={AVAILABLE_TOOLS}
                  onCancel={() => setIsEditing(false)}
                  onSaved={() => setIsEditing(false)}
                />
              ) : (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Empresa" icon={<Building2 className="w-4 h-4" />} value={participant?.company || "No definido"} />
                <InfoField label="Área" icon={<Target className="w-4 h-4" />} value={participant?.area || "No definido"} />
                <InfoField label="Cohorte" icon={<Users className="w-4 h-4" />} value={cohort?.name || "Sin cohorte"} />
                <InfoField label="Fecha de Creación" icon={<Calendar className="w-4 h-4" />} value={projectRow.created_at?.split("T")[0] || "—"} />
                <InfoField label="Público Objetivo" icon={<Users className="w-4 h-4" />} value={projectRow.target_audience || "No definido"} />
                <InfoField label="Propietario" icon={<Users className="w-4 h-4" />} value={participant?.full_name || "No definido"} />
                <InfoField label="Estado" icon={<Rocket className="w-4 h-4" />} value={statusLabels[projectStatus] || projectStatus} />
                <InfoField
                  label="Entrega"
                  icon={<Clock className="w-4 h-4" />}
                  value={(projectRow as any).submitted
                    ? `Entregado${(projectRow as any).submitted_at ? ` · ${String((projectRow as any).submitted_at).split("T")[0]}` : ""}`
                    : "Sin entregar"}
                />
                <InfoField
                  label="Aprobación"
                  icon={<CheckCircle2 className="w-4 h-4" />}
                  value={
                    (projectRow as any).approved_at
                      ? `Aprobado · ${String((projectRow as any).approved_at).split("T")[0]}`
                      : (projectRow as any).rejected_at
                        ? `Rechazado · ${String((projectRow as any).rejected_at).split("T")[0]}${(projectRow as any).rejection_reason ? ` — ${(projectRow as any).rejection_reason}` : ""}`
                        : "Sin revisar"
                  }
                />
                <InfoField label="Origen" icon={<Lightbulb className="w-4 h-4" />} value={(projectRow as any).origin || "—"} />
                <InfoField
                  label="Visibilidad"
                  icon={<EyeIcon className="w-4 h-4" />}
                  value={[
                    projectRow.is_org_visible ? "Organización" : null,
                    projectRow.is_public ? "Pública" : null,
                  ].filter(Boolean).join(" · ") || "Privada"}
                />
                <InfoField
                  label="Frecuencia de Medición"
                  icon={<Calendar className="w-4 h-4" />}
                  value={projectRow.measurement_frequency || "No definida"}
                />
              </div>


              {collaborators.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" /> Colaboradores ({collaborators.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {collaborators.map((c) => (
                        <Badge key={c.id} variant="secondary" className="gap-1.5 text-sm py-1 px-3">
                          <User className="w-3 h-3" />
                          <span>{c.name || c.email}</span>
                          {c.name && c.email && <span className="text-muted-foreground text-xs">· {c.email}</span>}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" /> Problema
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-lg">{projectRow.problem || "No definido"}</p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> Descripción de la Solución
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-lg">{projectRow.solution_description || "No definido"}</p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Database className="w-4 h-4 text-muted-foreground" /> Datos Usados
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-lg">{projectRow.data_used || "No definido"}</p>
              </div>

              {/* Tools */}
              {tools.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-primary" /> Herramientas
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {tools.map((toolId: string) => {
                      const tool = AVAILABLE_TOOLS[toolId];
                      return (
                        <Badge key={toolId} variant="secondary" className="gap-1.5 text-sm py-1 px-3">
                          <span>{tool?.icon || "🔧"}</span> {tool?.name || toolId}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KPI */}
              {projectRow.kpi_name && (
                <div className="space-y-3 p-4 rounded-xl border border-primary/20 bg-primary/5">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" /> KPI de Impacto
                  </h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground">KPI</p>
                      <p className="font-medium text-foreground text-sm">{projectRow.kpi_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Valor Actual</p>
                      <p className="font-bold text-destructive">{projectRow.kpi_current_value || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Valor Objetivo</p>
                      <p className="font-bold text-success">{projectRow.kpi_target_value || "—"}</p>
                    </div>
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Rúbrica ── */}
        <TabsContent value="rubric" className="space-y-6">
          <ProjectRubricAnswers projectId={projectRow.id} answers={rubricAnswers} readOnly={isAuditor} />
        </TabsContent>

        {/* ── Tab: Riesgos ── */}
        <TabsContent value="risks" className="space-y-6">
          <ProjectRisksTab
            projectId={projectRow.id}
            projectName={projectRow.name}
            organizationId={orgIdForCurrency}
            readOnly={isAuditor}
          />
        </TabsContent>


        {/* ── Tab: Entregable ── */}
        <TabsContent value="deliverable" className="space-y-6">
          {/* Project file (file_url) */}
          {projectRow.file_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <File className="w-5 h-5 text-primary" />
                  Archivo del Proyecto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <File className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-foreground truncate">
                      {(() => {
                        try {
                          const url = new URL(projectRow.file_url!);
                          const parts = url.pathname.split('/');
                          return decodeURIComponent(parts[parts.length - 1]);
                        } catch {
                          return 'Archivo adjunto';
                        }
                      })()}
                    </h5>
                    <p className="text-xs text-muted-foreground">
                      {projectRow.submitted_at ? `Enviado: ${new Date(projectRow.submitted_at).toLocaleDateString("es-ES")}` : "Adjunto al proyecto"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={async () => {
                      try {
                        const signed = await signDeliverableUrl(projectRow.file_url!);
                        window.open(signed, '_blank', 'noopener,noreferrer');
                      } catch (err: any) {
                        toast.error(err?.message || 'No se pudo descargar el archivo');
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resumen de rúbrica (detalle completo en la pestaña Rúbrica) */}
          <div className="flex items-center justify-between gap-3 p-4 rounded-lg border border-border bg-muted/20">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">
                {completedAnswers}/{LIDER_IA_COMPONENTS.length} preguntas de la rúbrica completadas
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setActiveTab("rubric")}>
              Ver rúbrica
            </Button>
          </div>

        </TabsContent>

        {/* ── Tab: Conversación ── */}
        <TabsContent value="conversation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" />
                Conversación que originó el proyecto
              </CardTitle>
              <CardDescription>
                {sourceSession ? (
                  <span className="flex flex-wrap items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px]">
                      {sourceSession.context_type === "challenge" ? "Lab. Retos y Soluciones" : "Agente de Prototipado"}
                    </Badge>
                    <span>{sourceSession.title || "Sin título"}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      {new Date(sourceSession.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </span>
                ) : (
                  "Conversación asociada a la creación del proyecto"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!sourceSessionId ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Este proyecto no tiene una conversación asociada.</p>
                  <p className="text-xs mt-1">Los proyectos creados manualmente (sin pasar por el Lab. de Retos y Soluciones) no tienen conversación de origen.</p>
                </div>
              ) : msgsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : conversationMessages.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">Sin mensajes en esta conversación</div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {conversationMessages.map((m: any) => (
                    <div
                      key={m.id}
                      className={`rounded-lg p-3 text-sm ${
                        m.role === "user"
                          ? "bg-primary/10 ml-8"
                          : m.role === "assistant"
                          ? "bg-muted mr-8"
                          : "bg-amber-500/10 text-xs italic"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] font-medium uppercase text-muted-foreground flex items-center gap-1">
                          {m.role === "user" ? <><User className="w-3 h-3" />Usuario</> : m.role === "assistant" ? <><Bot className="w-3 h-3" />Asistente</> : m.role}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(m.created_at).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: ROI ── */}
        <TabsContent value="roi" className="space-y-6">
          <RoiTab projectId={projectId!} roiData={projectRow.roi_data} currency={currency} readOnly={isAuditor} />
        </TabsContent>
      </Tabs>

      <BuildVsBuyDialog
        open={buildVsBuyOpen}
        onOpenChange={setBuildVsBuyOpen}
        projectId={projectId!}
        projectName={projectRow?.name}
      />
    </div>
  );
}

// ── ROI Tab Component ──────────────────────────────────────────
import { BENEFIT_TYPE_LABELS } from "@/data/roiModelData";

function normalizeCosts(costs: any): { label: string; amount: number }[] {
  if (!costs) return [];
  // New format: object with subscriptions, tokenConsumption, personnel, training
  if (typeof costs === "object" && !Array.isArray(costs) && ("subscriptions" in costs || "tokenConsumption" in costs || "personnel" in costs || "training" in costs)) {
    const labels: Record<string, string> = {
      subscriptions: "Suscripciones / Licencias",
      tokenConsumption: "Consumo de Tokens",
      personnel: "Capital Humano",
      training: "Capacitación",
    };
    return Object.entries(labels).map(([key, label]) => ({
      label,
      amount: (costs as any)[key] || 0,
    })).filter(c => c.amount > 0);
  }
  // Old format: array of { label, amount }
  if (Array.isArray(costs)) return costs;
  return [];
}

function normalizeBenefits(benefits: any): { label: string; amount: number }[] {
  if (!benefits || !Array.isArray(benefits)) return [];
  return benefits
    .filter((b: any) => b.type !== "not_defined" && b.amount > 0)
    .map((b: any) => ({
      label: b.label || (BENEFIT_TYPE_LABELS as any)[b.type] || b.type || "Sin nombre",
      amount: b.amount || 0,
    }));
}

function RoiTab({ projectId, roiData, currency, readOnly = false }: { projectId: string; roiData: any | null | undefined; currency: string; readOnly?: boolean }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <ProjectRoiEditor
        projectId={projectId}
        roiData={roiData}
        currency={currency}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );
  }

  const emptyState = (
    <Card>
      <CardContent className="py-12 text-center text-muted-foreground">
        <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">El participante no ha registrado datos de ROI aún</p>
        {!readOnly && (
          <Button className="mt-4 gradient-primary" size="sm" onClick={() => setEditing(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Crear datos de ROI
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (!roiData) return emptyState;


  const costItems = normalizeCosts(roiData.costs);
  const benefitItems = normalizeBenefits(roiData.benefits);

  if (costItems.length === 0 && benefitItems.length === 0) return emptyState;

  const totalCosts = costItems.reduce((s, c) => s + c.amount, 0);
  const totalBenefits = benefitItems.reduce((s, b) => s + b.amount, 0);
  const roi = totalCosts > 0 ? ((totalBenefits - totalCosts) / totalCosts) * 100 : 0;
  const bcr = totalCosts > 0 ? totalBenefits / totalCosts : 0;
  const paybackMonths = totalBenefits > 0 ? totalCosts / (totalBenefits / 12) : 0;
  const fmt = (n: number) => fmtCurrency(n, currency);

  return (
    <div className="space-y-6">
      {!readOnly && (
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="w-4 h-4 mr-1.5" /> Editar ROI
        </Button>
      </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6 text-center">
            <p className="text-xs text-muted-foreground mb-1">ROI</p>
            <p className={`text-2xl font-bold ${roi >= 0 ? "text-success" : "text-destructive"}`}>{roi.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-xs text-muted-foreground mb-1">BCR (Beneficio/Costo)</p>
            <p className="text-2xl font-bold text-foreground">{bcr.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-xs text-muted-foreground mb-1">Payback</p>
            <p className="text-2xl font-bold text-foreground">{paybackMonths.toFixed(1)} meses</p>
          </CardContent>
        </Card>
      </div>

      {/* Costs & Benefits tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <TrendingUp className="w-4 h-4" /> Costos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {costItems.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded bg-muted/40 text-sm">
                  <span className="text-foreground">{c.label}</span>
                  <span className="font-semibold text-destructive">{fmt(c.amount)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex items-center justify-between py-2 px-3 font-bold text-sm">
                <span>Total Costos</span>
                <span className="text-destructive">{fmt(totalCosts)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-success">
              <TrendingUp className="w-4 h-4" /> Beneficios
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {benefitItems.map((b, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded bg-muted/40 text-sm">
                  <span className="text-foreground">{b.label}</span>
                  <span className="font-semibold text-success">{fmt(b.amount)}</span>
                </div>
              ))}
              <Separator />
              <div className="flex items-center justify-between py-2 px-3 font-bold text-sm">
                <span>Total Beneficios</span>
                <span className="text-success">{fmt(totalBenefits)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
